'use client'

import React from 'react'
import {
  Bot,
  Shield,
  FileText,
  BarChart3,
  TrendingUp,
  CheckCircle2,
  Zap,
  BookOpen,
  Code,
  Activity,
  Target,
  ArrowRight,
  Quote,
  ListOrdered,
} from 'lucide-react'

/**
 * GEO Playbook Diagrams
 * Visual components for Part 3 of The AI Search Playbook
 * Brand colors: Primary #008dcc, Accent #FDC304, Pink #F90B8A, Purple #8b5cf6
 */

/**
 * AI Crawler Ecosystem - Shows all AI crawlers organized by platform
 */
export function AICrawlerEcosystem() {
  const platforms = [
    {
      name: 'OpenAI / ChatGPT',
      color: '#008dcc',
      bots: [
        { name: 'GPTBot', purpose: 'Training data', controllable: true },
        {
          name: 'OAI-SearchBot',
          purpose: 'Search results',
          controllable: true,
        },
        {
          name: 'ChatGPT-User',
          purpose: 'Real-time browsing',
          controllable: true,
        },
      ],
    },
    {
      name: 'Anthropic / Claude',
      color: '#8b5cf6',
      bots: [
        { name: 'ClaudeBot', purpose: 'Training data', controllable: true },
        { name: 'Claude-SearchBot', purpose: 'Web search', controllable: true },
        {
          name: 'Claude-User',
          purpose: 'User-initiated fetch',
          controllable: true,
        },
      ],
    },
    {
      name: 'Perplexity',
      color: '#FDC304',
      bots: [
        { name: 'PerplexityBot', purpose: 'Indexing', controllable: true },
        {
          name: 'Perplexity-User',
          purpose: 'Real-time retrieval',
          controllable: false,
        },
      ],
    },
    {
      name: 'Google',
      color: '#F90B8A',
      bots: [
        {
          name: 'Googlebot',
          purpose: 'Search + AI Overviews',
          controllable: true,
        },
        {
          name: 'Google-Extended',
          purpose: 'AI training (Gemini)',
          controllable: true,
        },
      ],
    },
  ]

  return (
    <div data-blog-layout='narrow'>
      <div className='my-8 p-6 md:p-8 bg-muted/50 rounded-2xl border border-border/60'>
        <div className='mb-6 flex items-center gap-2'>
          <Bot className='h-5 w-5 text-[#008dcc]' />
          <span className='text-lg font-semibold text-foreground'>
            AI Crawler Ecosystem: 10 Bots You Need to Know
          </span>
        </div>
        <div className='space-y-5'>
          {platforms.map(platform => (
            <div
              key={platform.name}
              className='rounded-lg border border-border/60 p-4'
            >
              <div className='mb-3 flex items-center gap-2'>
                <div
                  className='h-3 w-3 rounded-full'
                  style={{ backgroundColor: platform.color }}
                />
                <span className='font-semibold text-foreground'>
                  {platform.name}
                </span>
              </div>
              <div className='grid gap-2'>
                {platform.bots.map(bot => (
                  <div
                    key={bot.name}
                    className='flex items-center justify-between rounded-md bg-muted px-3 py-2'
                  >
                    <div className='flex items-center gap-2'>
                      <code
                        className='text-sm font-mono'
                        style={{ color: platform.color }}
                      >
                        {bot.name}
                      </code>
                    </div>
                    <div className='flex items-center gap-3'>
                      <span className='text-xs text-muted-foreground'>
                        {bot.purpose}
                      </span>
                      {bot.controllable && (
                        <span className='rounded-full bg-green-500/10 px-2 py-0.5 text-xs text-green-600'>
                          robots.txt
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
        <p className='mt-4 text-xs text-muted-foreground'>
          All bots marked with "robots.txt" can be individually allowed or
          blocked. Changes take ~24 hours to propagate.
        </p>
      </div>
    </div>
  )
}

/**
 * GEO Tactics Lift - Visual showing optimization tactics with lift percentages
 */
export function GEOTacticsLift() {
  const tactics = [
    {
      tactic: 'Add verifiable statistics',
      lift: 40,
      icon: BarChart3,
      example: '"Revenue grew 47% in Q3" beats "revenue grew significantly"',
      color: '#008dcc',
    },
    {
      tactic: 'Cite authoritative sources',
      lift: 40,
      icon: BookOpen,
      example: 'Link to primary research, not secondary blog posts',
      color: '#008dcc',
    },
    {
      tactic: 'Add expert quotations',
      lift: 35,
      icon: Quote,
      example: 'Named experts with credentials, not anonymous claims',
      color: '#8b5cf6',
    },
    {
      tactic: 'Optimize text fluency',
      lift: 25,
      icon: FileText,
      example: 'Clear, concise prose. One idea per paragraph.',
      color: '#FDC304',
    },
    {
      tactic: 'Use structured lists',
      lift: 25,
      icon: ListOrdered,
      example: 'Bullet points and numbered lists over dense paragraphs',
      color: '#FDC304',
    },
  ]

  return (
    <div data-blog-layout='narrow'>
      <div className='my-8 p-6 md:p-8 bg-muted/50 rounded-2xl border border-border/60'>
        <div className='mb-2 flex items-center gap-2'>
          <TrendingUp className='h-5 w-5 text-[#008dcc]' />
          <span className='text-lg font-semibold text-foreground'>
            GEO Optimization Tactics: Measured Visibility Lift
          </span>
        </div>
        <p className='mb-5 text-xs text-muted-foreground'>
          Source: Princeton/Georgia Tech GEO Study (KDD 2024), 10,000 queries
          tested
        </p>
        <div className='space-y-4'>
          {tactics.map(t => {
            const Icon = t.icon
            return (
              <div key={t.tactic}>
                <div className='mb-1 flex items-center justify-between'>
                  <div className='flex items-center gap-2'>
                    <Icon className='h-4 w-4' style={{ color: t.color }} />
                    <span className='text-sm font-medium text-foreground'>
                      {t.tactic}
                    </span>
                  </div>
                  <span
                    className='text-sm font-bold'
                    style={{ color: t.color }}
                  >
                    +{t.lift}%
                  </span>
                </div>
                <div className='mb-1 h-3 w-full overflow-hidden rounded-full bg-muted'>
                  <div
                    className='h-full rounded-full transition-all duration-500'
                    style={{
                      width: `${t.lift * 2.2}%`,
                      backgroundColor: t.color,
                      opacity: 0.8,
                    }}
                  />
                </div>
                <p className='text-xs text-muted-foreground italic'>
                  {t.example}
                </p>
              </div>
            )
          })}
        </div>
        <div className='mt-5 rounded-lg border border-[#008dcc]/20 bg-[#008dcc]/5 p-3'>
          <p className='text-sm text-foreground/80'>
            <strong>Best combination:</strong> Fluency + Statistics together
            outperform any single tactic by an additional 5.5%.
          </p>
        </div>
      </div>
    </div>
  )
}

/**
 * Technical GEO Checklist - Infrastructure requirements
 */
export function TechnicalGEOChecklist() {
  const items = [
    {
      category: 'Crawl Access',
      icon: Shield,
      color: '#008dcc',
      checks: [
        {
          label: 'robots.txt allows GPTBot, OAI-SearchBot, ChatGPT-User',
          critical: true,
        },
        {
          label: 'robots.txt allows ClaudeBot, Claude-SearchBot',
          critical: true,
        },
        { label: 'robots.txt allows PerplexityBot', critical: true },
        { label: 'Google-Extended allowed for Gemini', critical: false },
      ],
    },
    {
      category: 'Structured Data',
      icon: Code,
      color: '#8b5cf6',
      checks: [
        {
          label:
            'FAQPage schema on key pages (3.2x more likely in AI Overviews)',
          critical: true,
        },
        {
          label: 'Article schema with author and datePublished',
          critical: true,
        },
        { label: 'HowTo schema for instructional content', critical: false },
        { label: 'JSON-LD format (not Microdata)', critical: true },
      ],
    },
    {
      category: 'AI Discovery Files',
      icon: FileText,
      color: '#FDC304',
      checks: [
        { label: 'llms.txt at site root with key pages', critical: false },
        { label: 'llms-full.txt with complete content', critical: false },
        { label: 'XML sitemap with accurate lastmod dates', critical: true },
      ],
    },
    {
      category: 'Content Structure',
      icon: BookOpen,
      color: '#F90B8A',
      checks: [
        { label: 'Answer-first paragraphs (BLUF principle)', critical: true },
        {
          label: 'H1 > H2 > H3 hierarchy (40% more likely to be cited)',
          critical: true,
        },
        { label: 'Paragraphs under 120 words, one idea each', critical: false },
        {
          label: 'Tables for comparison data, lists for steps',
          critical: false,
        },
      ],
    },
  ]

  return (
    <div data-blog-layout='narrow'>
      <div className='my-8 p-6 md:p-8 bg-muted/50 rounded-2xl border border-border/60'>
        <div className='mb-6 flex items-center gap-2'>
          <CheckCircle2 className='h-5 w-5 text-[#008dcc]' />
          <span className='text-lg font-semibold text-foreground'>
            Technical GEO Checklist
          </span>
        </div>
        <div className='space-y-5'>
          {items.map(section => {
            const Icon = section.icon
            return (
              <div key={section.category}>
                <div className='mb-2 flex items-center gap-2'>
                  <Icon className='h-4 w-4' style={{ color: section.color }} />
                  <span className='font-semibold text-foreground'>
                    {section.category}
                  </span>
                </div>
                <div className='space-y-1.5 pl-6'>
                  {section.checks.map(check => (
                    <div key={check.label} className='flex items-start gap-2'>
                      <CheckCircle2 className='mt-0.5 h-3.5 w-3.5 shrink-0 text-green-500' />
                      <span className='text-sm text-foreground/80'>
                        {check.label}
                        {check.critical && (
                          <span className='ml-1.5 rounded bg-red-500/10 px-1.5 py-0.5 text-xs font-medium text-red-600'>
                            Critical
                          </span>
                        )}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )
          })}
        </div>
      </div>
    </div>
  )
}

/**
 * Case Study Cards - Three case studies with key metrics
 */
export function CaseStudyCards() {
  const studies = [
    {
      company: 'Go Fish Digital',
      metric: '3x',
      metricLabel: 'Lead increase in 3 months',
      conversion: '25x higher conversion vs traditional search',
      strategy: 'Prompt mapping, fact-dense content, query fan-out expansion',
      color: '#008dcc',
      icon: Target,
    },
    {
      company: 'The Rank Masters',
      metric: '8,337%',
      metricLabel: 'ChatGPT referral growth',
      conversion: '~50 pageviews per active user from ChatGPT',
      strategy: '42 pages in 3 months, semantic SEO, modular content',
      color: '#8b5cf6',
      icon: TrendingUp,
    },
    {
      company: 'Seer Interactive',
      metric: '15.9%',
      metricLabel: 'ChatGPT conversion rate',
      conversion: '9x higher than Google organic (1.76%)',
      strategy: 'Deep analysis proving AI traffic quality superiority',
      color: '#F90B8A',
      icon: BarChart3,
    },
  ]

  return (
    <div data-blog-layout='narrow'>
      <div className='my-8 space-y-4'>
        {studies.map(study => {
          const Icon = study.icon
          return (
            <div
              key={study.company}
              className='rounded-xl border border-border/60 bg-background p-5'
            >
              <div className='flex items-start justify-between'>
                <div>
                  <div className='mb-1 flex items-center gap-2'>
                    <Icon className='h-4 w-4' style={{ color: study.color }} />
                    <span className='text-sm font-medium text-muted-foreground'>
                      {study.company}
                    </span>
                  </div>
                  <div
                    className='text-3xl font-bold'
                    style={{ color: study.color }}
                  >
                    {study.metric}
                  </div>
                  <p className='text-sm font-medium text-foreground'>
                    {study.metricLabel}
                  </p>
                </div>
              </div>
              <div className='mt-3 space-y-1.5'>
                <div className='flex items-center gap-2'>
                  <Zap className='h-3.5 w-3.5 text-[#FDC304]' />
                  <span className='text-sm text-muted-foreground'>
                    {study.conversion}
                  </span>
                </div>
                <div className='flex items-center gap-2'>
                  <ArrowRight className='h-3.5 w-3.5 text-muted-foreground' />
                  <span className='text-xs text-muted-foreground'>
                    {study.strategy}
                  </span>
                </div>
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}

/**
 * AI Traffic Measurement - GA4 setup and measurement framework
 */
export function AITrafficMeasurement() {
  const steps = [
    {
      step: 1,
      title: 'Create AI Traffic Channel Group',
      description: 'Add custom channel in GA4 with regex matching AI referrers',
      detail:
        'chatgpt.com | perplexity.ai | claude.ai | gemini.google.com | copilot.microsoft.com',
      color: '#008dcc',
    },
    {
      step: 2,
      title: 'Set Up GA4 Explorations',
      description: 'Filter sessions by AI source domains for detailed analysis',
      detail:
        'Track conversion rate, pages/session, engagement time per AI platform',
      color: '#8b5cf6',
    },
    {
      step: 3,
      title: 'Monitor Citations',
      description: 'Track brand mentions across AI platforms weekly',
      detail: 'Manual testing + tools like Otterly.AI, Peec AI, or Sight AI',
      color: '#FDC304',
    },
    {
      step: 4,
      title: 'Benchmark and Compare',
      description: 'Compare AI traffic metrics against organic baselines',
      detail:
        'True AI influence is 2-3x what analytics reports (Seer Interactive)',
      color: '#F90B8A',
    },
  ]

  return (
    <div data-blog-layout='narrow'>
      <div className='my-8 p-6 md:p-8 bg-muted/50 rounded-2xl border border-border/60'>
        <div className='mb-6 flex items-center gap-2'>
          <Activity className='h-5 w-5 text-[#008dcc]' />
          <span className='text-lg font-semibold text-foreground'>
            AI Traffic Measurement Framework
          </span>
        </div>
        <div className='space-y-4'>
          {steps.map(s => (
            <div key={s.step} className='flex gap-4'>
              <div
                className='flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-sm font-bold text-white'
                style={{ backgroundColor: s.color }}
              >
                {s.step}
              </div>
              <div className='flex-1'>
                <h5 className='font-semibold text-foreground'>{s.title}</h5>
                <p className='text-sm text-muted-foreground'>{s.description}</p>
                <p className='mt-1 rounded bg-muted px-2 py-1 font-mono text-xs text-muted-foreground'>
                  {s.detail}
                </p>
              </div>
            </div>
          ))}
        </div>
        <div className='mt-5 rounded-lg border border-[#FDC304]/20 bg-[#FDC304]/5 p-3'>
          <p className='text-sm text-foreground/80'>
            <strong>Key insight:</strong> ChatGPT now appends{' '}
            <code className='text-xs'>utm_source=chatgpt.com</code> to citation
            links (since June 2025), making attribution easier than before.
          </p>
        </div>
      </div>
    </div>
  )
}
